import React, { useEffect, useState } from 'react';
import {
  AutoCompleteSelectUI,
  ButtonUI,
  CheckboxUI,
  ConfirmDialogUI,
  DatePickerUI,
  DateTimePickerUI,
  DialogUI,
  FormButtonPanelUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  SelectUI,
} from '../Interface/index.js';
import CancelIcon from '@mui/icons-material/Cancel';
import { Field, Form, Formik, useFormikContext } from 'formik';
import TemplateDialogTable from './TasklistDialogTable.jsx';
import { v4 as uuidv4 } from 'uuid';
import Yup from '../../utils/yup.js';
import dayjs from 'dayjs';
import TemplateImportDialog from '../TaskList/TaskListTab/TemplateImport/TemplateImportDialog.jsx';
import { Dependence, DependenceOption, TaskResponsibleTypeEnum } from '../../config/taskList.js';
import TaskListAttachmentDialog from './TaskListAttachmentDialog.jsx';
import minMax from 'dayjs/plugin/minMax';
import useParam from '../../context/ParamContext.js';
import { avg, getJokerFieldType } from '../../utils/helper.js';
import useUsers from '../../context/UsersContext.js';
import TaskListTabs from './TaskListTabs.jsx';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { JokerFieldTypes } from '../../config/joker.js';

dayjs.extend(minMax);

const TaskListDialog = ({
  open,
  handleClose,
  autoCompleteOptions,
  rows,
  defaultField,
  fromProject,
  autocompleteData,
  setRefetchTable,
  taskListJokerFields = [],
  submenuNames = [],
}) => {
  const apiRef = useGridApiRef();
  const { user } = useUsers();
  const { params } = useParam();
  const { setFieldValue, values } = useFormikContext();
  const [selectedValues, setSelectedValues] = useState({});
  const [selectedParentValues, setSelectedParentValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isAttachDialogOpen, setIsAttachDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  const baseDefaults = {
    isNew: true,
    name: '',
    responsiblePersonId: null,
    responsibleUserGroupId: null,
    responsibleCompanyId: null,
    responsibleWorkerId: null,
    responsibleType: TaskResponsibleTypeEnum.USER,
    duration: 1,
    waitingTime: 1,
    dependence: Dependence.FS,
    userGroupId: null,
    construction: true,
    userId: user?.userId,
    taskRatio: '',
    taskListRespPerson: [],
  };

  const defTaskListRespPerson = {
    id: null,
    isNew: true,
    userId: null,
  };

  const projectDefaults = {
    statusId: autocompleteData?.taskListStatus?.defaultValue ?? null,
    preparedness: autocompleteData?.taskListStatus?.defItem?.preparedness,
    startDate: dayjs(values.projectStartDate).format('YYYY-MM-DD'),
    typeId: null,
    description: '',
    deadLine: null,
    brigadeId: null,
    taskListResourceOne: [],
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    resourceThreeId: null,
    resourceFourId: null,
  };

  const defaultValues = {
    ...baseDefaults,
    ...(fromProject ? projectDefaults : {}),
  };

  useEffect(() => {
    if (!open || !fromProject || !apiRef?.current) return;
    if (Object.keys(selectedValues).length > 0) return;

    setTimeout(() => {
      const allRows = Array.from(apiRef.current.getRowModels().values());
      const parentId = selectedParentValues?.id ?? null;
      const { startDate: suggestedStart, wait } = computeNextStartAndWait(allRows, parentId, values.projectStartDate);

      setInitValues((prev) => ({
        ...prev,
        startDate: suggestedStart.format('YYYY-MM-DD'),
        waitingTime: wait,
      }));
    }, 0);
  }, [open, fromProject, apiRef, selectedParentValues, values.projectStartDate, selectedValues]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    responsiblePersonId: Yup.string().nullable(),
    responsibleUserGroupId: Yup.string().nullable(),
    responsibleCompanyId: Yup.string().nullable(),
    responsibleWorkerId: Yup.string().nullable(),
    responsibleType: Yup.string().required(),
    duration: Yup.number().positive().required(),
    waitingTime: Yup.number().positive().nullable(),
    userGroupId: Yup.string().nullable(),
    dependence: Yup.string().required(),
    construction: Yup.boolean(),
    userId: Yup.string().nullable(),
    taskRatio: Yup.number().positive().nullable(),
    taskListRespPerson: Yup.array().of(
      Yup.object().shape({
        userId: Yup.string().required(),
      })
    ),
    ...(fromProject && {
      startDate: Yup.string().required(),
      statusId: Yup.string().required(),
      preparedness: Yup.number().positive().nullable(),
      typeId: Yup.string().nullable(),
      description: Yup.string().nullable(),
      deadLine: Yup.string().nullable(),
      brigadeId: Yup.number().nullable(),
      taskListResourceOne: Yup.array()
        .of(
          Yup.object().shape({
            label: Yup.string().required(),
            value: Yup.number().required(),
          })
        )
        .when('brigadeId', {
          is: (brigadeId) => brigadeId != null,
          then: (schema) => schema.min(1),
          otherwise: (schema) => schema,
        }),
    }),
    ...(taskListJokerFields.length > 0
      ? Object.fromEntries(
          taskListJokerFields.map((field) => {
            const fieldType = getJokerFieldType(field);
            return [
              field.field,
              fieldType === JokerFieldTypes.NUMBER ? Yup.number().nullable() : Yup.string().nullable(),
            ];
          })
        )
      : {}),
    resourceThreeId: Yup.string().nullable(),
    resourceFourId: Yup.string().nullable(),
  });

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      setInitValues({
        ...selectedValues,
        preparedness: Number.isFinite(selectedValues.preparedness)
          ? selectedValues.preparedness
          : projectDefaults.preparedness,
        waitingTime: selectedValues.waitingTime ? parseFloat(selectedValues.waitingTime) : '',
        taskRatio: selectedValues.taskRatio || baseDefaults.taskRatio,
      });
    } else {
      setInitValues(defaultValues);
    }
  }, [selectedValues, autocompleteData, user?.userId]);

  const resolveInheritedResponsible = (parentId, rowsMap) => {
    let current = parentId ? rowsMap.get(parentId) : null;
    while (current) {
      if (current.responsiblePersonId) {
        return current.responsiblePersonId;
      }
      current = current.parentId ? rowsMap.get(current.parentId) : null;
    }
    return null;
  };

  const computeNextStartAndWait = (rows, parentId, projectStart) => {
    const siblings = rows.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);

    if (siblings.length === 0) {
      const base = dayjs(projectStart).startOf('day');
      return { startDate: base, wait: 1 };
    }

    const last = siblings[siblings.length - 1];
    const lastStart = dayjs(last.startDate);
    const lastEnd = lastStart.add((parseFloat(last.duration) || 1) - 1, 'day');

    const wait =
      Number.isFinite(parseFloat(last?.waitingTime)) && parseFloat(last.waitingTime) > 0
        ? parseFloat(last.waitingTime)
        : 1;

    return { startDate: lastEnd.add(wait, 'day'), wait };
  };

  const getNextStartReference = (allRows, parentId, projectStart) => {
    const siblings = allRows.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);

    if (siblings.length === 0) {
      const base = dayjs(projectStart).startOf('day');
      return { hasSibling: false, referenceEnd: base, defaultWait: 0 };
    }

    const last = siblings[siblings.length - 1];
    const lastStart = dayjs(last.startDate);
    const lastEnd = lastStart.add((parseFloat(last.duration) || 1) - 1, 'day');

    const defaultWait =
      Number.isFinite(parseFloat(last?.waitingTime)) && parseFloat(last.waitingTime) >= 0
        ? parseFloat(last.waitingTime)
        : 1;

    return { hasSibling: true, referenceEnd: lastEnd, defaultWait };
  };

  const onWaitingTimeChange = async (rawVal, setFieldValue, parentId) => {
    const allRows = Array.from(apiRef.current.getRowModels().values());
    const { hasSibling, referenceEnd } = getNextStartReference(allRows, parentId, values.projectStartDate);

    if (rawVal === '' || rawVal === null) {
      await setFieldValue('waitingTime', null);
      return;
    }

    const n = parseFloat(rawVal, 10);
    const wait = Number.isFinite(n) ? n : 0;

    if (wait === 0) {
      await setFieldValue('waitingTime', null);
      if (hasSibling) {
        const newStart = referenceEnd.format('YYYY-MM-DD');
        await setFieldValue('startDate', newStart);
      }
      return;
    }

    await setFieldValue('waitingTime', wait);
    if (!hasSibling) return;

    if (Object.keys(selectedValues).length === 0) {
      const newStart = referenceEnd.add(wait, 'day').format('YYYY-MM-DD');
      await setFieldValue('startDate', newStart);
    }
  };

  const onStartDateChange = async (newDayjs, setFieldValue, parentId) => {
    if (!newDayjs) {
      await setFieldValue('startDate', null);
      return;
    }
    const allRows = Array.from(apiRef.current.getRowModels().values());
    const { referenceEnd } = getNextStartReference(allRows, parentId, values.projectStartDate);

    const start = dayjs(newDayjs).startOf('day');
    const diffDays = Math.max(0, start.diff(referenceEnd.startOf('day'), 'day'));
    await setFieldValue('startDate', start.format('YYYY-MM-DD'));
    await setFieldValue('waitingTime', diffDays === 0 ? 1 : diffDays);
  };

  const calculateStartDateWithDependency = (dependence, duration, refTask, opts = {}) => {
    const { refIsParent = false, isFirstChild = false } = opts;
    if (!refTask?.startDate) return dayjs();

    const refStart = dayjs(refTask.startDate);
    const refEnd = refStart.add(parseFloat(refTask?.duration) - 1 || 0, 'day');

    const wait =
      Number.isFinite(parseFloat(refTask?.waitingTime)) && parseFloat(refTask.waitingTime) > 0
        ? parseFloat(refTask.waitingTime)
        : 1;

    if (refIsParent && isFirstChild) {
      return refStart;
    }

    if (dependence === 'FS') {
      return refEnd.add(wait, 'day');
    } else if (dependence === 'SF') {
      return refStart.subtract(duration, 'day');
    }
    return refStart;
  };

  const useRatio = params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y';

  const recomputeParentPreparednessChain = (rows, startingParentId) => {
    let parentId = startingParentId;

    while (parentId) {
      const children = rows.filter((r) => r.parentId === parentId);
      if (children.length > 0) {
        let newPrep = 0;

        if (useRatio) {
          const weightedSum = children.reduce((sum, c) => {
            return sum + Number(c.taskRatio ?? 0) * (Number.isFinite(c.preparedness) ? c.preparedness : 0);
          }, 0);
          newPrep = Math.round(weightedSum / 100);
        } else {
          const preparedList = children.map((c) => (Number.isFinite(c.preparedness) ? c.preparedness : 0));
          newPrep = avg(preparedList);
        }

        const idx = rows.findIndex((r) => r.id === parentId);
        if (idx !== -1) {
          const parent = rows[idx];
          rows[idx] = { ...parent, preparedness: newPrep };
          parentId = parent.parentId ?? null;
          continue;
        }
      }
      break;
    }

    return rows;
  };

  const updateParentChain = (rows, changedTask) => {
    let parentId = changedTask.parentId;
    const nextRows = [...rows];

    while (parentId) {
      const children = nextRows.filter((r) => r.parentId === parentId);
      if (children.length === 0) break;

      const earliest = dayjs.min(children.map((c) => dayjs(c.startDate)));
      const latestEnd = dayjs.max(children.map((c) => dayjs(c.startDate).add((c.duration || 1) - 1, 'day')));

      const parentIdx = nextRows.findIndex((r) => r.id === parentId);
      if (parentIdx === -1) break;

      nextRows[parentIdx] = {
        ...nextRows[parentIdx],
        startDate: earliest.format('YYYY-MM-DD') + 'T00:00:00.000Z',
        duration: latestEnd.diff(earliest, 'day') + 1,
      };

      parentId = nextRows[parentIdx].parentId;
    }
    return nextRows;
  };

  const handleCreate = async (values, resetForm) => {
    const allRows = Array.from(apiRef.current.getRowModels().values());
    const rowsMap = new Map(allRows.map((r) => [r.id, r]));
    const newId = uuidv4();

    const parentId = selectedParentValues?.id ?? null;
    const newPath = selectedParentValues?.path ? [...selectedParentValues.path, newId] : [newId];

    const siblingRows = allRows.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);

    const lastSibling = siblingRows[siblingRows.length - 1];
    const depType = values.dependence;

    const hasExplicitStart = !!values.startDate;
    let startDateToUse;
    let waitingToUse = values.waitingTime;

    if (hasExplicitStart) {
      startDateToUse = dayjs(values.startDate).startOf('day');

      if (lastSibling) {
        const lastStart = dayjs(lastSibling.startDate);
        const lastEnd = lastStart.add((parseFloat(lastSibling.duration) || 1) - 1, 'day');
        const diff = Math.max(0, startDateToUse.diff(lastEnd.startOf('day'), 'day'));
        waitingToUse = Number.isFinite(diff) ? diff : null;
      } else {
        if (waitingToUse === '') waitingToUse = null;
      }
    } else {
      if (lastSibling) {
        startDateToUse = calculateStartDateWithDependency(depType, values.duration, lastSibling);
        if (waitingToUse === '') {
          const w = parseFloat(lastSibling?.waitingTime);
          waitingToUse = Number.isFinite(w) ? w : 1;
        }
      } else if (selectedParentValues?.startDate) {
        startDateToUse = calculateStartDateWithDependency(depType, values.duration, selectedParentValues, {
          refIsParent: true,
          isFirstChild: true,
        });
        if (waitingToUse === '' || waitingToUse === undefined) waitingToUse = null;
      } else {
        startDateToUse = dayjs(values.projectStartDate).startOf('day');
        if (waitingToUse === '' || waitingToUse === undefined) waitingToUse = null;
      }
    }

    let responsibleToUse = values.responsiblePersonId;
    if (!responsibleToUse && params.TASKLIST_INHERITE_RESP_PERSON === 'Y') {
      responsibleToUse = resolveInheritedResponsible(parentId, rowsMap);
    }

    const newItem = {
      ...defaultField,
      ...values,
      id: newId,
      visOrder: siblingRows.length + 1,
      parentId,
      path: newPath,
      duration: values.duration ? parseFloat(values.duration) : null,
      startDate: startDateToUse.format('YYYY-MM-DD') + 'T00:00:00.000Z',
      waitingTime: waitingToUse === 0 ? null : waitingToUse,
      responsiblePersonId: responsibleToUse,
      preparedness: parseFloat(values.preparedness),
      taskRatio: parseFloat(values.taskRatio),
    };

    const updatedSiblings = [...siblingRows, newItem].map((item, idx) => ({ ...item, visOrder: idx + 1 }));
    const updatedRows = [...allRows.filter((row) => row.parentId !== parentId), ...updatedSiblings];

    const recalculatedRows = updateParentChain(updatedRows, newItem);
    const withPreparedParents = recomputeParentPreparednessChain(recalculatedRows, newItem.parentId);

    apiRef.current.updateRows(withPreparedParents);
    setSelectedValues({});
    setInitValues(defaultValues);
    resetForm();
  };

  const handleUpdate = (values) => {
    const { id } = selectedValues;

    if (!id) return;

    const allRows = Array.from(apiRef.current.getRowModels().values());
    const parentId = selectedValues?.parentId ?? null;

    const updatedStart = dayjs(values.startDate);
    const updatedEnd = updatedStart.add(values.duration - 1, 'day');

    const wtParsed = parseFloat(values.waitingTime, 10);
    const waitingNorm = Number.isFinite(wtParsed) && wtParsed > 0 ? wtParsed : null;
    const firstGap = waitingNorm ?? 0;

    const updatedItem = {
      ...selectedValues,
      ...values,
      startDate: updatedStart.format('YYYY-MM-DD') + 'T00:00:00.000Z',
      waitingTime: waitingNorm,
      preparedness: parseFloat(values.preparedness),
      taskRatio: parseFloat(values.taskRatio),
    };

    const siblingRows = allRows.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);

    const updatedIndex = siblingRows.findIndex((r) => r.id === id);

    const before = siblingRows.slice(0, updatedIndex);

    const updatedSiblings = [...before, updatedItem];

    let currentStart = updatedEnd.add(firstGap, 'day');

    for (let i = updatedIndex + 1; i < siblingRows.length; i++) {
      const sibling = siblingRows[i];
      const newStart = currentStart;
      const newEnd = newStart.add(sibling.duration - 1, 'day');

      const updatedSibling = {
        ...sibling,
        startDate: newStart.format('YYYY-MM-DD') + 'T00:00:00.000Z',
      };

      updatedSiblings.push(updatedSibling);

      const gap =
        Number.isFinite(parseFloat(sibling.waitingTime)) && parseFloat(sibling.waitingTime) > 0
          ? parseFloat(sibling.waitingTime)
          : 1;

      currentStart = newEnd.add(gap, 'day');
    }

    let afterSiblingShift = [...allRows.filter((r) => r.parentId !== parentId), ...updatedSiblings];

    if (
      selectedValues.responsiblePersonId !== values.responsiblePersonId &&
      params.TASKLIST_INHERITE_RESP_PERSON === 'Y'
    ) {
      const rowsMap = new Map(afterSiblingShift.map((r) => [r.id, r]));

      const updateChildrenInherited = (pid) => {
        const children = afterSiblingShift.filter((r) => r.parentId === pid);

        for (const child of children) {
          if (child.responsiblePersonId) continue;

          const newResp = resolveInheritedResponsible(child.parentId, rowsMap);

          const idx = afterSiblingShift.findIndex((r) => r.id === child.id);
          const updatedChild = {
            ...afterSiblingShift[idx],
            responsiblePersonId: newResp,
          };
          afterSiblingShift[idx] = updatedChild;

          rowsMap.set(updatedChild.id, updatedChild);

          updateChildrenInherited(updatedChild.id);
        }
      };

      updateChildrenInherited(id);
    }

    if (fromProject && selectedValues.statusId !== values.statusId) {
      const statusList = autocompleteData?.taskListStatus?.statusItems ?? [];
      const chosen = statusList.find((s) => s?.itemId === values.statusId);

      const chosenPreparedness = parseFloat(chosen?.preparedness);
      afterSiblingShift = afterSiblingShift.map((row) => {
        const isParent = row.id === id;
        const isChild = row.path.includes(id);

        if (isParent) {
          return {
            ...row,
            statusId: values.statusId,
            preparedness: chosenPreparedness,
          };
        }

        if (isChild && chosenPreparedness === 100) {
          return {
            ...row,
            statusId: values.statusId,
            preparedness: chosenPreparedness,
          };
        }

        return row;
      });
    }

    const recalculatedRows = updateParentChain(afterSiblingShift, updatedItem);
    const withPreparedParents = recomputeParentPreparednessChain(recalculatedRows, updatedItem.parentId);

    apiRef.current.updateRows(withPreparedParents);
    setSelectedValues({});
    setInitValues(defaultValues);
  };

  const handleSubmit = (values, { resetForm }) => {
    if (Object.keys(selectedValues).length > 0) {
      handleUpdate(values);
    } else {
      handleCreate(values, resetForm);
    }
  };

  const onClose = () => {
    handleClose();
    setSelectedValues({});
    setInitValues(defaultValues);
    setSelectedParentValues({});
  };

  const closeWithoutSave = () => {
    if (fromProject) {
      const currentRows = Array.from(apiRef.current.getRowModels().values());
      const updatedList = rows.map((item) => {
        const gridRow = currentRows.find((row) => row.id === item.id);
        if (gridRow && gridRow.taskListAttachment) {
          return {
            ...item,
            taskListAttachment: gridRow.taskListAttachment,
          };
        }
        return item;
      });
      setFieldValue('taskList', updatedList);
    }

    onClose();
  };

  const handleSave = () => {
    const rows = Array.from(apiRef.current.getRowModels().values());

    if (fromProject) {
      setFieldValue('taskList', rows);
    } else {
      setFieldValue('taskListTemplateItem', rows);
    }

    onClose();
  };

  const handleAddItemFromTemplate = (items) => {
    const existingRows = Array.from(apiRef.current.getRowModels().values());
    const idMap = new Map();

    const itemsWithNewIds = items.map((item) => {
      const newId = uuidv4();
      idMap.set(item.id, newId);
      return {
        ...item,
        id: newId,
      };
    });

    let combinedRows = [...existingRows];

    const enrichedItems = itemsWithNewIds.map((item) => {
      const newParentId = item.parentId ? idMap.get(item.parentId) ?? null : null;
      const newPath = item.path ? item.path.map((id) => idMap.get(id)).filter(Boolean) : [item.id];

      const siblings = combinedRows.filter((r) => r.parentId === newParentId);
      const lastSibling = siblings[siblings.length - 1];
      let startDate = dayjs(values.projectStartDate);

      if (lastSibling) {
        startDate = calculateStartDateWithDependency(item.dependence, item.duration ?? 1, lastSibling);
      } else if (newParentId) {
        const parentRow = combinedRows.find((r) => r.id === newParentId);
        if (parentRow?.startDate) {
          startDate = calculateStartDateWithDependency(item.dependence, item.duration ?? 1, parentRow, {
            refIsParent: true,
            isFirstChild: true,
          });
        }
      }

      let responsibleToUse = item.responsiblePersonId ?? null;
      if (!responsibleToUse && params.TASKLIST_INHERITE_RESP_PERSON === 'Y') {
        responsibleToUse = resolveInheritedResponsible(newParentId, new Map(combinedRows.map((r) => [r.id, r])));
      }

      const enriched = {
        ...item,
        isNew: true,
        parentId: newParentId,
        path: newPath,
        duration: item.duration ?? 1,
        waitingTime: item.waitingTime ?? 1,
        startDate: startDate.format('YYYY-MM-DD') + 'T00:00:00.000Z',
        statusId: defaultValues.statusId,
        responsiblePersonId: responsibleToUse,
        userId: user?.userId,
      };

      combinedRows.push(enriched);
      return enriched;
    });

    const uniqueParentIds = [...new Set(enrichedItems.map((item) => item.parentId ?? null))];
    uniqueParentIds.forEach((parentId) => {
      const siblings = combinedRows.filter((r) => r.parentId === parentId).sort((a, b) => a.visOrder - b.visOrder);
      siblings.forEach((item, idx) => {
        item.visOrder = idx + 1;
      });
    });

    enrichedItems.forEach((item) => {
      combinedRows = updateParentChain(combinedRows, item);
    });

    setSelectedValues({});
    apiRef.current.updateRows(combinedRows);
  };

  const showAttachDialog = (attachments) => {
    setSelectedRow(attachments);
    setIsAttachDialogOpen(true);
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  const showBrigadeManagement = params.TASKLIST_SHOW_BRIGADE_MANAGEMENT === 'Y';

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      fullWidth
      headerContent={
        <div className="flex items-center justify-between">
          <div>{fromProject ? 'Feladat lista' : 'Feladat sablon'}</div>
          <div>
            <CancelIcon sx={{ fontSize: '2rem', cursor: 'pointer' }} onClick={onClose} />
          </div>
        </div>
      }
      fullScreen
    >
      <div className="grid grid-cols-12 mt-5 gap-5">
        <div className="col-span-4">
          <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnBlur={false}
            validateOnMount={false}
          >
            {({ resetForm, values, errors, setFieldValue, validateField }) => {
              const hasResponsible =
                values.responsiblePersonId != null ||
                values.responsibleUserGroupId != null ||
                values.responsibleCompanyId != null ||
                values.responsibleWorkerId != null;

              const hasUserGroup = values.userGroupId != null;
              const hasBrigade = showBrigadeManagement && values.brigadeId != null;
              const hasWorkers = showBrigadeManagement && (values.taskListResourceOne?.length ?? 0) > 0;

              return (
                <Form>
                  <div className="grid grid-cols-3 gap-x-4">
                    <div className="md:col-span-2 col-span-3">
                      <Field
                        name="name"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Megnevezés" />}
                        helperText={<FormErrorUI message={errors.name} />}
                        variant="standard"
                        InputLabelProps={{ shrink: values.name && values.name !== '' }}
                      />
                    </div>
                    <div className={`md:col-span-1 col-span-3 ${!fromProject ? 'hidden' : ''}`}>
                      <Field
                        name="startDate"
                        variant="standard"
                        as={DatePickerUI}
                        fullWidth
                        label={<FormLabelUI text="Kezdő dátum" />}
                        value={dayjs(values.startDate)}
                        helperText={<FormErrorUI message={errors.startDate} />}
                        onChange={(newVal) =>
                          onStartDateChange(newVal, setFieldValue, selectedParentValues?.id ?? null)
                        }
                      />
                    </div>
                    {params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y' && (
                      <div className="md:col-span-1 col-span-3">
                        <Field
                          type="number"
                          name="taskRatio"
                          as={InputUI}
                          fullWidth
                          label={<FormLabelUI text="Feladat arány" />}
                          helperText={<FormErrorUI message={errors.taskRatio} />}
                          variant="standard"
                          InputLabelProps={{ shrink: values.taskRatio && values.taskRatio !== '' }}
                        />
                      </div>
                    )}
                    <div className="md:col-span-1 col-span-3">
                      <Field
                        type="number"
                        name="duration"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Időintervallum (nap)" />}
                        helperText={<FormErrorUI message={errors.duration} />}
                        variant="standard"
                        InputLabelProps={{ shrink: values.duration && values.duration !== '' }}
                      />
                    </div>
                    <div className="md:col-span-1 col-span-3">
                      <Field
                        type="number"
                        name="waitingTime"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Várakozási idő (nap)" />}
                        helperText={<FormErrorUI message={errors.duration} />}
                        variant="standard"
                        InputLabelProps={{ shrink: values.waitingTime && values.waitingTime !== '' }}
                        onChange={(e) =>
                          onWaitingTimeChange(e?.target?.value, setFieldValue, selectedParentValues?.id ?? null)
                        }
                      />
                    </div>
                    <div className={`col-span-3 md:col-span-1 ${!fromProject ? 'hidden' : ''}`}>
                      <Field
                        type="autocomplete"
                        name="statusId"
                        component={AutoCompleteSelectUI}
                        fullWidth
                        label={<FormLabelUI text="Státusz" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.statusId} />}
                        onChange={(_e, newVal) => {
                          setFieldValue('statusId', newVal?.value ?? null).then(() => {
                            validateField('statusId');
                          });
                        }}
                        InputLabelProps={{ shrink: values.statusId !== '' }}
                        selectedValue={values.statusId}
                        selectedLabelValue={
                          autoCompleteOptions?.taskListStatus?.find((status) => status.value === values.statusId)?.label
                        }
                        dataset={autoCompleteOptions?.taskListStatus}
                      />
                    </div>

                    <div className="col-span-3 md:col-span-1">
                      <Field
                        type="autocomplete"
                        name="responsible"
                        component={AutoCompleteSelectUI}
                        fullWidth
                        label={<FormLabelUI text="Felelős" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.responsiblePersonId} />}
                        onChange={(_e, newVal) => {
                          const type = newVal?.type || TaskResponsibleTypeEnum.USER;
                          const value = newVal?.value || null;

                          setFieldValue('responsibleType', type);

                          // reset
                          setFieldValue('responsiblePersonId', null);
                          setFieldValue('responsibleUserGroupId', null);
                          setFieldValue('responsibleCompanyId', null);
                          setFieldValue('responsibleWorkerId', null);

                          if (!value) return;

                          const fieldByType = {
                            [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
                            [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
                            [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
                            [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
                          };

                          const field = fieldByType[type];
                          if (!field) return;

                          setFieldValue(field, value).then(() => {
                            if (field === 'responsiblePersonId') {
                              validateField('responsiblePersonId');
                            }
                          });
                        }}
                        InputLabelProps={{
                          shrink:
                            values.responsiblePersonId !== '' ??
                            values.responsibleUserGroupId !== '' ??
                            values.responsibleCompanyId !== '' ??
                            values.responsibleWorkerId !== '',
                        }}
                        selectedValue={
                          values.responsiblePersonId ??
                          values.responsibleUserGroupId ??
                          values.responsibleCompanyId ??
                          values.responsibleWorkerId
                        }
                        selectedLabelValue={
                          autoCompleteOptions?.responsible?.find(
                            (r) =>
                              r.value ===
                                (values.responsiblePersonId ??
                                  values.responsibleUserGroupId ??
                                  values.responsibleCompanyId ??
                                  values.responsibleWorkerId) && r.type === values.responsibleType
                          )?.label
                        }
                        disabled={hasUserGroup || hasBrigade || hasWorkers}
                        dataset={autoCompleteOptions?.responsible}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Field
                        type="autocomplete"
                        name="userGroupId"
                        component={AutoCompleteSelectUI}
                        fullWidth
                        label={<FormLabelUI text="Felelősök (csoport)" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.userGroupId} />}
                        onChange={(_e, newVal) => {
                          setFieldValue('userGroupId', newVal?.value ?? null).then(() => {
                            validateField('userGroupId');
                          });
                        }}
                        InputLabelProps={{ shrink: values.userGroupId !== '' }}
                        selectedValue={values.userGroupId}
                        selectedLabelValue={
                          autoCompleteOptions?.userGroups?.find((user) => user.value === values.userGroupId)?.label
                        }
                        disabled={hasResponsible || hasBrigade || hasWorkers}
                        dataset={autoCompleteOptions?.userGroups}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Field
                        type="autocomplete"
                        name="userId"
                        component={AutoCompleteSelectUI}
                        fullWidth
                        label={<FormLabelUI text="Tulajdonos" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.userId} />}
                        onChange={(_e, newVal) => {
                          setFieldValue('userId', newVal?.value ?? null).then(() => {
                            validateField('userId');
                          });
                        }}
                        InputLabelProps={{ shrink: values.userId !== '' }}
                        selectedValue={values.userId}
                        selectedLabelValue={
                          autoCompleteOptions?.user?.find((user) => user.value === values.userId)?.label
                        }
                        dataset={autoCompleteOptions?.user}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Field
                        name="dependence"
                        component={SelectUI}
                        fullWidth
                        option={DependenceOption}
                        value={values.dependence ?? ''}
                        variant="standard"
                        onChange={(e) => {
                          const value = e.target.value;
                          setFieldValue('dependence', value ?? null).then(() => {
                            validateField('dependence');
                          });
                        }}
                        label={<FormLabelUI text="Függőség" />}
                        helperText={<FormErrorUI message={errors.dependence} />}
                        InputLabelProps={{ shrink: values.dependence !== '' }}
                      />
                    </div>
                    <div className={`md:col-span-1 col-span-3 ${!fromProject ? 'hidden' : ''}`}>
                      <Field
                        name="preparedness"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Készültség" />}
                        helperText={<FormErrorUI message={errors.preparedness} />}
                        variant="standard"
                        InputLabelProps={{ shrink: values.preparedness && values.preparedness !== '' }}
                      />
                    </div>
                    <div className={`col-span-3 md:col-span-1 ${!fromProject ? 'hidden' : ''}`}>
                      <Field
                        type="autocomplete"
                        name="typeId"
                        component={AutoCompleteSelectUI}
                        fullWidth
                        label={<FormLabelUI text="Típus" />}
                        variant="standard"
                        helperText={<FormErrorUI message={errors.typeId} />}
                        onChange={(_e, newVal) => {
                          setFieldValue('typeId', newVal?.value ?? null).then(() => {
                            validateField('typeId');
                          });
                        }}
                        InputLabelProps={{ shrink: values.typeId !== '' }}
                        selectedValue={values.typeId}
                        selectedLabelValue={
                          autoCompleteOptions?.taskListType?.find((status) => status.value === values.typeId)?.label
                        }
                        dataset={autoCompleteOptions?.taskListType}
                      />
                    </div>
                    <div className={`md:col-span-1 col-span-3 ${!fromProject ? 'hidden' : ''}`}>
                      <Field
                        name="deadLine"
                        variant="standard"
                        as={DateTimePickerUI}
                        fullWidth
                        label={<FormLabelUI text="Határidő" />}
                        value={dayjs(values.deadLine)}
                        helperText={<FormErrorUI message={errors.deadLine} />}
                        onChange={(newVal) => {
                          setFieldValue(`deadLine`, newVal ?? null);
                        }}
                      />
                    </div>
                    {params.TASKLIST_SHOW_RESOURCETHREE_FIELD === 'Y' && (
                      <div className={`col-span-3 md:col-span-1 ${!fromProject ? 'hidden' : ''}`}>
                        <Field
                          type="autocomplete"
                          name="resourceThreeId"
                          component={AutoCompleteSelectUI}
                          fullWidth
                          label={<FormLabelUI text={submenuNames[2] ?? 'Erőforrás 3'} />}
                          variant="standard"
                          helperText={<FormErrorUI message={errors.resourceThreeId} />}
                          onChange={(_e, newVal) => {
                            setFieldValue('resourceThreeId', newVal?.value ?? null).then(() => {
                              validateField('resourceThreeId');
                            });
                          }}
                          InputLabelProps={{ shrink: values.resourceThreeId !== '' }}
                          selectedValue={values.resourceThreeId}
                          selectedLabelValue={
                            autoCompleteOptions?.resourceThree?.find((r3) => r3.value === values.resourceThreeId)?.label
                          }
                          dataset={autoCompleteOptions?.resourceThree}
                        />
                      </div>
                    )}
                    {params.TASKLIST_SHOW_RESOURCEFOUR_FIELD === 'Y' && (
                      <div className={`col-span-3 md:col-span-1 ${!fromProject ? 'hidden' : ''}`}>
                        <Field
                          type="autocomplete"
                          name="resourceFourId"
                          component={AutoCompleteSelectUI}
                          fullWidth
                          label={<FormLabelUI text={submenuNames[3] ?? 'Erőforrás 4'} />}
                          variant="standard"
                          helperText={<FormErrorUI message={errors.resourceFourId} />}
                          onChange={(_e, newVal) => {
                            setFieldValue('resourceFourId', newVal?.value ?? null).then(() => {
                              validateField('resourceFourId');
                            });
                          }}
                          InputLabelProps={{ shrink: values.resourceFourId !== '' }}
                          selectedValue={values.resourceFourId}
                          selectedLabelValue={
                            autoCompleteOptions?.resourceFour?.find((r4) => r4.value === values.resourceFourId)?.label
                          }
                          dataset={autoCompleteOptions?.resourceFour}
                        />
                      </div>
                    )}
                    <div className={`md:col-span-3 col-span-3 ${!fromProject ? 'hidden' : ''}`}>
                      <Field
                        name="description"
                        as={InputUI}
                        fullWidth
                        label={<FormLabelUI text="Megjegyzés" />}
                        helperText={<FormErrorUI message={errors.description} />}
                        variant="standard"
                        InputLabelProps={{ shrink: values.description && values.description !== '' }}
                        rows={2}
                        multiline
                      />
                    </div>
                    {showBrigadeManagement && (
                      <>
                        <div className={`md:col-span-1 col-span-3 ${!fromProject ? 'hidden' : ''}`}>
                          <Field
                            type="autocomplete"
                            name="brigadeId"
                            component={AutoCompleteSelectUI}
                            fullWidth
                            label={<FormLabelUI text="Brigád" />}
                            variant="standard"
                            helperText={<FormErrorUI message={errors.brigadeId} />}
                            onChange={(_e, newVal) => {
                              const brigadeId = newVal?.value ?? null;

                              const workers = (autocompleteData.resourceOne ?? [])
                                .filter((item) => item.statusId === brigadeId && item.active)
                                .map((item) => ({
                                  label: item.name,
                                  value: item.id,
                                }));

                              setFieldValue('brigadeId', brigadeId);
                              setFieldValue('taskListResourceOne', workers);
                            }}
                            InputLabelProps={{ shrink: values.brigadeId != null }}
                            selectedValue={values.brigadeId}
                            selectedLabelValue={
                              autoCompleteOptions?.brigade?.find((item) => item.value === values.brigadeId)?.label
                            }
                            dataset={autoCompleteOptions?.brigade}
                            disabled={hasResponsible || hasUserGroup}
                          />
                        </div>
                        <div
                          className={`md:col-span-2 col-span-3 flex items-center gap-2 ${!fromProject ? 'hidden' : ''}`}
                        >
                          <Field
                            type="autocomplete"
                            name="taskListResourceOne"
                            component={AutoCompleteSelectUI}
                            fullWidth
                            label={<FormLabelUI text="Dolgozók" />}
                            variant="standard"
                            helperText={<FormErrorUI message={errors.taskListResourceOne} />}
                            onChange={(_e, newVal) => {
                              setFieldValue('taskListResourceOne', newVal ?? []).then(() => {
                                validateField('taskListResourceOne');
                              });
                            }}
                            InputLabelProps={{ shrink: Boolean(values.taskListResourceOne?.length) }}
                            selectedValue={values.taskListResourceOne ?? []}
                            multiple
                            freeSolo
                            getOptionLabel={(option) => option.label}
                            dataset={autoCompleteOptions?.resourceOne}
                            disabled={hasResponsible || hasUserGroup}
                          />

                          <span className="mt-4">{values.taskListResourceOne?.length ?? 0}</span>
                        </div>
                      </>
                    )}
                    <div className="md:col-span-2 col-span-4 pt-2">
                      <Field
                        name="construction"
                        type="checkbox"
                        as={CheckboxUI}
                        fullWidth
                        label={<FormLabelUI text="Kivitelezés" />}
                      />
                    </div>
                  </div>

                  <TaskListTabs
                    defTaskListRespPerson={defTaskListRespPerson}
                    autoCompleteOptions={autoCompleteOptions}
                    fromProject={fromProject}
                  />

                  <div className="flex justify-center mt-5">
                    <FormButtonPanelUI
                      onCancelClick={() => {
                        resetForm();
                        setSelectedValues({});
                      }}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="col-span-8">
          <TemplateDialogTable
            autoCompleteOptions={autoCompleteOptions}
            rows={rows}
            apiRef={apiRef}
            setSelectedValues={setSelectedValues}
            setSelectedParentValues={setSelectedParentValues}
            disabled={Object.keys(selectedParentValues).length === 0}
            fromProject={fromProject}
            setIsImportDialogOpen={setIsImportDialogOpen}
            showAttachDialog={showAttachDialog}
            autocompleteData={autocompleteData}
            recomputeParentPreparednessChain={recomputeParentPreparednessChain}
            setConfirmDialogData={setConfirmDialogData}
            onCloseConfirmDialog={onCloseConfirmDialog}
            taskListJokerFields={taskListJokerFields}
            submenuNames={submenuNames}
          />
        </div>
      </div>
      <div className="flex justify-center mt-5 gap-5">
        <ButtonUI text="Mentés és bezárás" className="bg-success" onClick={handleSave} />
        <ButtonUI text="Mégsem és bezárás" className="bg-danger" onClick={closeWithoutSave} />
      </div>
      <TemplateImportDialog
        open={isImportDialogOpen}
        handleClose={() => setIsImportDialogOpen(false)}
        onItemAdd={handleAddItemFromTemplate}
        autocompleteData={autocompleteData}
        autoCompleteOptions={autoCompleteOptions}
      />
      <TaskListAttachmentDialog
        open={isAttachDialogOpen}
        apiRef={apiRef}
        handleClose={() => setIsAttachDialogOpen(false)}
        setRefetchTable={setRefetchTable}
        user={user}
        row={{ selectedRow, setSelectedRow }}
        editable={true}
      />

      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog()}
        message={confirmDialogData.message}
      />
    </DialogUI>
  );
};

export default TaskListDialog;
